
import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface WaveSyncStatusProps {
  clientId: string;
  waveCustomerId?: string;
}

const WaveSyncStatus: React.FC<WaveSyncStatusProps> = ({
  clientId,
  waveCustomerId
}) => {
  const { toast } = useToast();
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [isManualSync, setIsManualSync] = useState(false);

  const handleManualSync = async () => {
    if (!waveCustomerId) {
      toast({
        title: 'Sync Not Available',
        description: 'Billing integration is not configured for your account.',
        variant: 'destructive'
      });
      return;
    }

    setIsManualSync(true);
    setSyncStatus('syncing');

    try {
      // Trigger billing data sync for this customer
      const { error } = await supabase.functions.invoke('sync-customer-wave-data', {
        body: { customerId: waveCustomerId, clientId }
      });

      if (error) throw error;

      setSyncStatus('success');
      setLastSyncAt(new Date().toISOString());
      
      toast({
        title: 'Sync Completed',
        description: 'Your billing data has been synchronized successfully.',
      });
    } catch (error: any) {
      console.error('Manual sync failed:', error);
      setSyncStatus('error');
      
      toast({
        title: 'Sync Failed',
        description: 'Unable to sync your billing data. Please try again later.',
        variant: 'destructive'
      });
    } finally {
      setIsManualSync(false);
    }
  };

  const getSyncStatusBadge = () => {
    switch (syncStatus) {
      case 'syncing':
        return (
          <Badge className="bg-blue-500">
            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
            Syncing
          </Badge>
        );
      case 'success':
        return (
          <Badge className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Synced
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-500">
            <AlertCircle className="h-3 w-3 mr-1" />
            Sync Error
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <Clock className="h-3 w-3 mr-1" />
            Ready
          </Badge>
        );
    }
  };

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-blue-600" />
            Billing Sync Status
          </span>
          {getSyncStatusBadge()}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {lastSyncAt && (
            <div className="text-sm text-gray-600">
              Last synced: {new Date(lastSyncAt).toLocaleString()}
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualSync}
            disabled={isManualSync || syncStatus === 'syncing'}
            className="w-full"
          >
            {isManualSync ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
          </Button>
          
          {waveCustomerId && (
            <div className="text-xs text-gray-500 text-center">
              Connected to Billing System (ID: {waveCustomerId.slice(-8)})
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WaveSyncStatus;
